import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useState, useContext, useEffect } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import InputIcon from "react-multi-date-picker/components/input_icon";
import { unLocalizeNumbers, localizeNumbers } from "../../functions";
import CustomerApi from "../../api/customer";
import LineApi from "../../api/line";
import Cities from "./data/cities.json";
import Provinces from "./data/provinces.json";
import Countries from "./data/countries.json";
import { useTranslation } from "react-i18next";
import AuthContext from "../auth-context";
import PhoneInfo from "../../types/phone-info";
import NgnLineSpecsApi from "../../api/ngn-line-specs";
import UserInfoApi from "../../api/user-info";
import ShopCustomerApi from "../../api/shop-customer";
import MediumApi, { MediumDto } from "../../api/medium";

export interface ICustomerData {
  type: number;
  username: string;
  phone?: string;
  cellPhone?: string;
  serviceType: string;
  agentCode: string;
  subCenterId: string;
  cityReferenceDataId: number | undefined;
}

export interface ICustomerInfoProps {
  type?: number;
  onSubmit: (customerData: ICustomerData) => void;
  authId?: string;
  handleAgentDataChange: (data: AgentData) => void;
  isShop: boolean;
  defaultCellPhone?: string;
  shopSelectedType?: number;
}

interface AgentData {
  name: string | null;
  code: number | null;
  isSupportUser: boolean;
  hasSubAgents: boolean;
}

export interface ngnProvinces {
  [id: string]: string;
}

interface FormValues {
  phone?: string;
  isMale: boolean;
  firstName: string;
  lastName: string;
  nationalCode: string;
  fatherName: string;
  postalCode: string;
  provinces: string;
  countryCode: number;
  city: string;
  byStreet: string;
  lastAlley: string;
  email?: string;
  cellPhone: string;
  type: number;
  plaqueNo: string;
  unitNo: string;
  agentCode?: string;

  company?: {
    name: string;
    nationalIdentity: string;
    registrationDate: number;
  };
}

export default function CustomerInfo({
  handleAgentDataChange,
  onSubmit,
  isShop,
  defaultCellPhone,
  shopSelectedType,
  type = isShop ? (shopSelectedType ?? 30) : 30,
}: ICustomerInfoProps) {
  const [selectedType, setSelectedType] = useState<number>(Number(type) || 30);
  const [birthDate, setBirthDate] = useState<DateObject | null>(null);
  const [registrationDate, setRegisterDateTime] = useState<DateObject | null>(null);
  const [selectedProvince, setSelectedProvince] = useState<string>();
  const [selectedCountry, setSelectedCountry] = useState<number>();
  const [selectedCity, setSelectedCity] = useState<any>();
  const [selectedNgnProvince, setSelectedNgnProvince] = useState<number | null>(null);
  const [subCenter, setSelectedSubCenter] = useState<string>();
  const [phoneInfo, setphoneInfo] = useState<PhoneInfo | null>();
  const context = useContext(AuthContext);
  const [isCompany, setIsCompany] = useState(false);
  const [isForeigner, setIsForeigner] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const [checkBoxToggle, setcheckBoxToggle] = useState(false);
  const [toggle, setToggle] = useState(true);
  const [apiResponse, setApiResponse] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const { t } = useTranslation(undefined, { keyPrefix: "SignUp.CustomerInfo" });
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [nationalCode, setNationalCode] = useState<string>("");
  const [nationalIdentity, setNationalIdentity] = useState<string>("");
  const [cellPhone, setCellPhone] = useState<string>("");
  const [ngnProvinces, setNgnProvinces] = useState<ngnProvinces>({});
  const [userData, setUserData] = useState<AgentData>({
    name: null,
    code: null,
    isSupportUser: false,
    hasSubAgents: false,
  });
  const [mediums, setMediums] = useState<MediumDto[]>([]);

  const maxDate = new DateObject({ calendar: persian }).subtract(18, "years");

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      type: Number(type) || 30,
    },
  });

  useEffect(() => {
    if (type !== undefined && type !== null) {
      setSelectedType(Number(type));
      setValue("type", Number(type));
    }
  }, [type, setValue]);

  const handleSelectType = (selectedMediumId: number) => {
    setSelectedType(selectedMediumId);
    setValue("type", selectedMediumId);

    if (selectedMediumId === 30) navigate("/signup/tdlte");
    else if (selectedMediumId === 31) navigate("/signup/fdlte");
    else if (selectedMediumId === 0) navigate("/signup/adsl");
    else if (selectedMediumId === 41 || selectedMediumId === 7) navigate("/signup/package");
    else if (selectedMediumId === 40) navigate("/signup/ngn");
  };

  const handleName = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFirstName(e.target.value);
  const handleLastName = (e: React.ChangeEvent<HTMLInputElement>) =>
    setLastName(e.target.value);
  const handleNationalCode = (e: React.ChangeEvent<HTMLInputElement>) =>
    setNationalCode(unLocalizeNumbers(e.target.value));
  const handleCellPhone = (e: React.ChangeEvent<HTMLInputElement>) =>
    setCellPhone(unLocalizeNumbers(e.target.value));
  const handleNationalIdentity = (e: React.ChangeEvent<HTMLInputElement>) =>
    setNationalIdentity(unLocalizeNumbers(e.target.value));

  const handleNgnProvince = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const numericValue = value ? Number(value) : null;
    setSelectedNgnProvince(numericValue);
    context.setNgnSelectedProvince(numericValue);
  };

  const handleSubCenter = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setSelectedSubCenter(event.target.value);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const validPhone = new RegExp("^0?[1-8][0-9]{9}$");
    const phone = unLocalizeNumbers(e.target.value);
    if (!validPhone.test(phone)) return;

    new LineApi(context)
      .get(phone)
      .then((data) => {
        setphoneInfo(data);
        if (data.errorMessage != null) {
          setApiResponse("error");
          setPhoneError(data.errorMessage);
          setcheckBoxToggle(true);
        } else {
          setApiResponse("success");
          setcheckBoxToggle(false);
        }
      })
      .catch();
  };

  const getServiceTypeName = (mediumType: number) => {
    switch (mediumType) {
      case 40:
        return "NGN";
      case 41:
        return "SubscriptionPackage";
      case 0:
        return "ADSL";
      case 30:
        return "TD-LTE";
      case 31:
        return "FD-LTE";
      default:
        return "TD-LTE";
    }
  };

  const onFormSubmit = (formData: FormValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const submissionData: any = { ...formData };

    if (submissionData.phone) submissionData.phone = unLocalizeNumbers(submissionData.phone);
    submissionData.plaqueNo = unLocalizeNumbers(submissionData.plaqueNo);
    submissionData.unitNo = unLocalizeNumbers(submissionData.unitNo);
    submissionData.nationalCode = unLocalizeNumbers(submissionData.nationalCode);
    submissionData.cellPhone = unLocalizeNumbers(submissionData.cellPhone);
    submissionData.postalCode = unLocalizeNumbers(submissionData.postalCode);

    if (submissionData.company?.nationalIdentity) {
      submissionData.company.nationalIdentity = unLocalizeNumbers(
        submissionData.company.nationalIdentity
      );
    }

    if (isCompany && registrationDate) {
      submissionData.company = {
        ...submissionData.company,
        registrationDate: +unLocalizeNumbers(registrationDate.format("YYYYMMDD")),
      };
    }

    const currentServiceType = getServiceTypeName(selectedType);

    const data: any = {
      ...submissionData,
      type: selectedType,
      birthDate: birthDate ? +unLocalizeNumbers(birthDate.format("YYYYMMDD")) : undefined,
      serviceType: currentServiceType,
      subCenterId: subCenter,
      cityReferenceDataId: selectedCity,
    };

    if (!isCompany) {
      delete data.company;
    }

    context.setNgnSelectedProvince(selectedNgnProvince!);

    const apiHandler = isShop
      ? new ShopCustomerApi().put(data)
      : new CustomerApi().post(data);

    apiHandler
      .then((res: any) => {
        if (selectedType !== 0) delete data.phone;
        if (!res?.errorMessage) {
          onSubmit({
            username: isCompany
              ? data.company?.nationalIdentity!
              : data.cellPhone!,
            cellPhone: isShop ? defaultCellPhone! : data?.cellPhone!,
            phone: data.type !== 0 ? data.cellPhone : data.phone,
            type: +data.type!,
            serviceType: data.serviceType,
            agentCode: userData.code != null ? String(userData.code) : String(data.agentCode ?? ""),
            subCenterId: data.subCenterId!,
            cityReferenceDataId: data.cityReferenceDataId,
          });
        } else {
          setErrorMessage(res.errorMessage);
        }
      })
      .catch(() => setErrorMessage("خطایی هنگام ارسال اطلاعات رخ داد."))
      .finally(() => setIsSubmitting(false));
  };

  useEffect(() => {
    const api = new MediumApi(context);

    api
      .getAll()
      .then((data: MediumDto[]) => {
        const normalizedData = (data || []).map((m: any) => ({
          ...m,
          id: m.id ?? m.Id,
          title: m.title ?? m.Title,
        }));

        const filtered = isShop
          ? normalizedData.filter((m) => m.id !== 0)
          : normalizedData;

        setMediums(filtered);
      })
      .catch((err) => {
        console.error("Failed to load mediums:", err);
      });
  }, [isShop]);

  useEffect(() => {
    const agentApi = new UserInfoApi(context);
    agentApi.get().then((data) => {
      if (data) {
        setUserData({
          name: data.name,
          code: data.code,
          isSupportUser: data.isSupportUser,
          hasSubAgents: data.hasSubAgents,
        });
        handleAgentDataChange({
          name: data.name,
          code: data.code,
          isSupportUser: data.isSupportUser,
          hasSubAgents: data.hasSubAgents,
        });
      }
    });

    const productTypeApi = new NgnLineSpecsApi(context);
    productTypeApi.get(10).then((result) => {
      if (result?.specs?.[7]) {
        setNgnProvinces(result.specs[7].values);
      }
    });
    // eslint-disable-next-line
  }, []);

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="signup-section">
      <div className="md:w-full lg:w-full mx-auto xs:p-0 mt-10 p-10 rounded-xl">
        {!isShop && (
          <>
            <h3 className="lead-title pt-2">{t("Service type")}</h3>
            <div className="w-full">
              <div className="mx-auto rtl pt-10 text-center flex justify-center signup-user-type-section signup-tab-section flex-wrap md:flex-nowrap">
                <div className="flex items-center mr-4 tab-title-section">
                  <h3 className="signup-form-ut-title">{t("Choose your service")}</h3>
                </div>

                {mediums?.map((item) => {
                  const isChecked = selectedType === item.id;

                  return (
                    <div key={item.id} className="flex items-center mr-4">
                      <input
                        type="radio"
                        {...register("type")}
                        id={`medium-${item.id}`}
                        value={item.id}
                        className="hidden"
                        checked={isChecked}
                        onChange={() => handleSelectType(item.id)}
                      />
                      <label
                        htmlFor={`medium-${item.id}`}
                        className={`cursor-pointer flex items-center transition-all ${
                          isChecked ? "tab-active" : ""
                        }`}
                        onClick={() => handleSelectType(item.id)}
                      >
                        <div className="title">
                          <strong>{item.title}</strong>
                          <span className="w-4 h-4 inline-block mr-1 rounded-full border border-grey"></span>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {selectedType === 40 && (
          <div>
            <div className="company-section">
              <h3 className="lead-title">{t("NgnUsage Province")}</h3>
              <div className="form-section rtl flex flex-grow xs:flex-col sm:flex-row gap-4">
                <div className="flex flex-col xs:w-full sm:w-1/3 rtl text-right mt-6 relative">
                  <label htmlFor="" className="pr-4">
                    {t("Province")}
                  </label>
                  <select
                    value={selectedNgnProvince ?? ""}
                    className="mt-2"
                    tabIndex={13}
                    onChange={handleNgnProvince}
                  >
                    <option value="" disabled>
                      {t("choose your province")}
                    </option>
                    {Object.entries(ngnProvinces).map(([id, name]) => (
                      <option value={id} key={id}>
                        {name}
                      </option>
                    ))}
                  </select>
                  <p className="absolute left-0 text-red-500">
                    {errors.provinces?.message}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedType === 0 ? (
          <div className="company-section">
            <h3 className="lead-title">{t("ADSL Phone number")}</h3>
            <div className="form-section rtl mt-2 flex flex-grow xs:flex-col lg:flex-row xl:flex-row gap-4">
              <div className="form-group flex flex-col xs:w-full sm:w-full lg:w-2/4 text-right mt-6 relative">
                <label htmlFor="" className="pr-4">
                  {t("Phone number")}
                </label>
                <input
                  {...register("phone", {
                    required: "این فیلد الزامی است",
                    maxLength: { value: 11, message: "کمتر از 11 رقم" },
                  })}
                  type="tel"
                  name="phone"
                  className="text-ws-2 border-2 p-4 rounded-lg mt-2"
                  placeholder={t("enter your phone number")}
                  tabIndex={1}
                  onChange={handlePhoneChange}
                />
                <p className="absolute left-0 text-red-500">
                  {errors.phone?.message}
                </p>
              </div>
              {apiResponse === "error" ? (
                <p className="text-red-500">{phoneError}</p>
              ) : (
                selectedType === 0 &&
                apiResponse === "success" && (
                  <div className="form-group flex flex-row flex-wrap justify-between xs:w-full sm:w-full lg:w-3/4 text-right mt-6 relative">
                    <div className="xs:w-1/2">
                      <h5 className="pb-5">شهر :</h5>
                      <span>{phoneInfo?.city}</span>
                    </div>
                    <div className="xs:w-1/2">
                      <label className="pr-4">
                        {`مرکز مخابراتی ${phoneInfo?.center}`} :
                      </label>
                      {phoneInfo?.subCentersList?.length !== 0 ? (
                        <select
                          className="mt-2 xs:w-full"
                          onChange={handleSubCenter}
                        >
                          <option value="0">هیچکدام</option>
                          {phoneInfo?.subCentersList?.map(({ id, title }) => (
                            <option key={id} value={id}>
                              {title}
                            </option>
                          ))}
                        </select>
                      ) : null}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        ) : null}

        <div className="w-full">
          <div className="mx-auto rtl pt-10 text-center flex justify-center signup-user-type-section flex-wrap md:flex-nowrap signup-tab-section">
            <div className="flex items-center mr-4 tab-title-section">
              <h3 className="signup-form-ut-title">{t("User type")}</h3>
            </div>
            <div className="flex items-center mr-4">
              <input
                id="radio1"
                type="radio"
                name="userType"
                className="hidden"
                checked={!isCompany}
                onChange={() => setIsCompany(false)}
              />
              <label
                htmlFor="radio1"
                className={
                  isCompany
                    ? "flex items-center cursor-pointer"
                    : "tab-active flex items-center cursor-pointer"
                }
                onClick={() => setIsCompany(false)}
              >
                <span className="sepanta-icon-user"></span>
                <div className="title">
                  <strong>{t("Person")}</strong>
                  <span className="w-4 h-4 inline-block mr-1 rounded-full border border-grey"></span>
                </div>
              </label>
            </div>
            <div className="flex items-center mr-4">
              <input
                id="radio2"
                type="radio"
                name="userType"
                className="hidden"
                checked={isCompany}
                onChange={() => setIsCompany(true)}
                tabIndex={2}
              />
              <label
                htmlFor="radio2"
                className={
                  isCompany
                    ? "tab-active flex items-center cursor-pointer"
                    : "flex items-center cursor-pointer"
                }
                onClick={() => setIsCompany(true)}
              >
                <span className="sepanta-icon-company"></span>
                <div className="title">
                  <strong>{t("Company")}</strong>
                  <span className="w-4 h-4 inline-block mr-1 rounded-full border border-grey"></span>
                </div>
              </label>
            </div>
          </div>
        </div>

        {isCompany ? (
          <div className="company-section mb-2">
            <h3 className="lead-title">{t("Company Info")}</h3>
            <div className="form-section rtl mt-2 flex flex-grow xs:flex-col lg:flex-row xl:flex-row gap-4">
              <div className="form-group flex flex-col sm:w-full lg:w-2/4 text-right mt-6 relative">
                <label htmlFor="company.name" className="pr-4">
                  {t("Company name")} *
                </label>
                <input
                  {...register("company.name", {
                    required: isCompany ? t("required") : false,
                  })}
                  className="border-2 p-4 rounded-lg mt-2"
                  placeholder={t("enter company name")}
                  tabIndex={3}
                />
                <p className="absolute left-0 text-red-500">
                  {errors.company?.name?.message}
                </p>
              </div>

              <div className="form-group flex flex-col sm:w-full lg:w-2/4 rtl text-right mt-6 relative">
                <label htmlFor="company.nationalIdentity" className="pr-4">
                  {t("Company national identity")} *
                </label>
                <input
                  {...register("company.nationalIdentity", {
                    required: isCompany ? t("required") : false,
                    pattern: {
                      value: /^[0-9]*$/,
                      message: t("national number is not valid"),
                    },
                    minLength: {
                      value: 11,
                      message: t("کمتر از 11 رقم"),
                    },
                    maxLength: {
                      value: 11,
                      message: "",
                    },
                  })}
                  type="tel"
                  maxLength={11}
                  className="border-2 p-4 rounded-lg mt-2"
                  placeholder={t("enter national identity of company")}
                  tabIndex={4}
                  onChange={handleNationalIdentity}
                />

                <p className="absolute left-0 text-red-500">
                  {errors.company?.nationalIdentity?.message}
                </p>
              </div>

              <div className="form-group flex flex-col sm:w-full lg:w-2/4 rtl text-right mt-6 relative">
                <label htmlFor="" className="pr-4">
                  {t("Register date")} *
                </label>
                <div className="flex flex-row gap-2 mt-2">
                  <DatePicker
                    inputClass="full-size-dp"
                    className="rmdp-mobile"
                    render={<InputIcon tabIndex={9} />}
                    calendar={persian}
                    locale={persian_fa}
                    value={registrationDate}
                    calendarPosition="bottom-right"
                    onChange={(d) => {
                      if (Array.isArray(d)) d = d[0];
                      setRegisterDateTime(d);
                    }}
                  />
                  <input type={"hidden"} value={registrationDate?.format()} />
                </div>
                {!registrationDate && (
                  <p className="absolute left-0 text-red-500">{t("required")}</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full">
            <div className="mx-auto rtl pt-10 text-center flex justify-center signup-user-type-section flex-wrap md:flex-nowrap signup-tab-section">
              <div className="flex items-center mr-4 tab-title-section">
                <h3 className="signup-form-ut-title">{t("nationality")}</h3>
              </div>
              <div className="flex items-center mr-4">
                <input
                  id="nationalityRadio1"
                  type="radio"
                  name="nationalityRadio"
                  className="hidden"
                  checked={!isForeigner}
                  onChange={() => setIsForeigner(false)}
                />
                <label
                  htmlFor="nationalityRadio1"
                  className={
                    isForeigner
                      ? "flex items-center cursor-pointer"
                      : "tab-active flex items-center cursor-pointer"
                  }
                  onClick={() => setIsForeigner(false)}
                >
                  <div className="title">
                    <strong>{t("Iranian")}</strong>
                    <span className="w-4 h-4 inline-block mr-1 rounded-full border border-grey"></span>
                  </div>
                </label>
              </div>
              <div className="flex items-center mr-4">
                <input
                  id="nationalityRadio2"
                  type="radio"
                  name="nationalityRadio"
                  className="hidden"
                  checked={isForeigner}
                  onChange={() => setIsForeigner(true)}
                  tabIndex={2}
                />
                <label
                  htmlFor="nationalityRadio2"
                  className={
                    isForeigner
                      ? "tab-active flex items-center cursor-pointer"
                      : "flex items-center cursor-pointer"
                  }
                  onClick={() => setIsForeigner(true)}
                >
                  <div className="title">
                    <strong>{t("nonIranian")}</strong>
                    <span className="w-4 h-4 inline-block mr-1 rounded-full border border-grey"></span>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}

        <h3 className="lead-title pt-2">{t("Personal info")}</h3>
        <br />
        {errorMessage && (
          <ul className="text-red-500 text-right">
            <li>{errorMessage}</li>
          </ul>
        )}
        <div className="form-section rtl mt-2 flex flex-grow xs:flex-col lg:flex-row xl:flex-row gap-4">
          <div className="form-group flex flex-col xs:w-full sm:w-1/3 rtl text-right mt-6 relative">
            <label htmlFor="" className="pr-4">
              {t("gender")} *
            </label>
            <select
              {...register("isMale", {
                required: t("required"),
              })}
              className="mt-2"
              tabIndex={13}
            >
              <option key="male" value="true">
                مرد
              </option>
              <option key="feMale" value="false">
                زن
              </option>
            </select>
          </div>

          <div className="form-group flex flex-col sm:w-full lg:w-3/4 xl:w-2/4 text-right mt-6 relative">
            <label htmlFor="" className="pr-4">
              {t("First name")}*
            </label>
            <input
              maxLength={20}
              {...register("firstName", {
                required: t("required"),
                maxLength: { value: 20, message: "" },
              })}
              type="text"
              name="firstName"
              className="border-2 p-4 rounded-lg mt-2"
              placeholder={t("enter your name")}
              tabIndex={5}
              onChange={handleName}
            />
            <p className="absolute left-0 text-red-500">
              {errors.firstName?.message}
            </p>
          </div>

          <div className="form-group flex flex-col sm:w-full lg:w-3/4 xl:w-2/4 text-right mt-6 relative">
            <label htmlFor="" className="pr-4">
              {t("Last name")}*
            </label>
            <input
              maxLength={30}
              tabIndex={6}
              {...register("lastName", {
                required: t("required"),
                maxLength: { value: 50, message: "بیشتر است" },
              })}
              type="text"
              name="lastName"
              className="border-2 p-4 rounded-lg mt-2"
              placeholder={t("enter your last name")}
              onChange={handleLastName}
            />
            <p className="absolute left-0 text-red-500">
              {errors.lastName?.message}
            </p>
          </div>
        </div>

        <div className="form-section rtl flex flex-grow xs:flex-col sm:flex-row gap-4">
          {isForeigner ? (
            <div className="form-group flex flex-col xs:w-full sm:w-1/3 rtl text-right mt-6 relative">
              <label htmlFor="" className="pr-4">
                {t("Country")} *
              </label>
              <select
                {...register("countryCode", {
                  required: isForeigner ? t("required") : false,
                })}
                value={selectedCountry ?? ""}
                className="mt-2"
                tabIndex={13}
                onChange={(e) => setSelectedCountry(+e.target.value)}
              >
                <option value="" disabled>
                  {t("choose your country")}
                </option>
                {Countries.map(({ title, id }) => (
                  <option value={id} key={id}>
                    {title}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="form-group flex flex-col xs:w-full sm:w-1/2 rtl text-right mt-6 relative">
              <label htmlFor="" className="pr-4">
                {t("Father name")} *
              </label>
              <input
                maxLength={20}
                tabIndex={8}
                {...register("fatherName", {
                  required: !isForeigner ? t("required") : false,
                })}
                type="text"
                className="border-2 p-4 rounded-lg mt-2 tracking-wider text-right"
                placeholder={t("enter your father name")}
              />
              <p className="absolute left-0 text-red-500">
                {errors.fatherName?.message}
              </p>
            </div>
          )}
          <div className="form-group flex flex-col sm:w-full lg:w-2/4 rtl text-right mt-6 relative">
            <label htmlFor="" className="pr-4">
              {t(isForeigner ? "Fida code" : "National code")} *
            </label>
            <input
              maxLength={15}
              tabIndex={7}
              {...register("nationalCode", {
                required: t("required"),
                minLength: { value: 3, message: "کمتر از 3 رقم" },
                maxLength: { value: 15, message: "" },
              })}
              type="tel"
              className="border-2 p-4 rounded-lg mt-2"
              placeholder={t(
                isForeigner
                  ? "enter your fida code"
                  : "enter your national code"
              )}
              onChange={handleNationalCode}
            />
            <p className="absolute left-0 text-red-500">
              {errors.nationalCode?.message}
            </p>
          </div>

          <div className="form-group flex flex-col xs:w-full sm:w-1/2 rtl text-right mt-6 relative">
            <label htmlFor="" className="pr-4">
              {t("Birth date")} *
            </label>
            <div className="flex flex-row gap-2 mt-2">
              <DatePicker
                inputClass="full-size-dp"
                className="rmdp-mobile"
                render={<InputIcon tabIndex={9} />}
                calendar={persian}
                locale={persian_fa}
                maxDate={maxDate}
                value={birthDate}
                calendarPosition="bottom-right"
                onChange={(d) => {
                  if (Array.isArray(d)) d = d[0];
                  setBirthDate(d);
                }}
              />
              <input type={"hidden"} value={birthDate?.format()} />
            </div>
            {!birthDate && <p className="absolute left-0 text-red-500">{t("required")}</p>}
          </div>
        </div>

        <h3 className="lead-title pt-10">{t("Contact info")}</h3>

        <div className="form-section rtl flex flex-grow xs:flex-col sm:flex-row gap-4">
          {!isShop && (
            <div className="form-group flex flex-col xs:w-full sm:w-1/3 rtl text-right mt-6 relative">
              <label htmlFor="" className="pr-4">
                {t("Cell number")} *
              </label>
              <input
                tabIndex={10}
                {...register("cellPhone", {
                  required: !isShop ? t("required") : false,
                  minLength: { value: 11, message: "کمتر از 11 رقم" },
                })}
                type="tel"
                className="border-2 p-4 rounded-lg mt-2 text-right tracking-wider"
                placeholder={t("enter your cell number")}
                onChange={handleCellPhone}
              />
              <p className="absolute left-0 text-red-500">
                {errors.cellPhone?.message}
              </p>
            </div>
          )}
          <div className="form-group flex flex-col xs:w-full sm:w-1/3 rtl text-right mt-6 relative">
            <label htmlFor="" className="pr-4">
              {t("Email")}
            </label>
            <input
              maxLength={40}
              tabIndex={11}
              {...register("email", {
                maxLength: {
                  value: 40,
                  message: t("maximum 40 characters allowed"),
                },
                pattern: {
                  value: /^([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})?$/i,
                  message: t("email is not valid"),
                },
              })}
              name="email"
              type="email"
              className={`border-2 p-4 rounded-lg mt-2 text-left tracking-wider ${
                errors.email ? "border-red-500" : ""
              }`}
              placeholder={t("enter your email")}
            />
            {errors.email && (
              <p className="absolute left-0 text-red-500">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="form-group flex flex-col xs:w-full sm:w-1/3 rtl text-right mt-6 relative">
            <label htmlFor="" className="pr-4">
              {t("Postal code")} *
            </label>
            <input
              tabIndex={12}
              maxLength={10}
              {...register("postalCode", {
                required: t("required"),
                maxLength: { value: 10, message: "کمتر از 10 رقم" },
              })}
              name="postalCode"
              className="border-2 p-4 rounded-lg mt-2 text-right tracking-wider"
              placeholder={t("enter your postal code")}
              type="tel"
            />
            <p className="absolute left-0 text-red-500">
              {errors.postalCode?.message}
            </p>
          </div>
        </div>

        <div className="form-section rtl flex flex-grow xs:flex-col sm:flex-row gap-4">
          <div className="form-group flex flex-col xs:w-full sm:w-1/3 rtl text-right mt-6 relative">
            <label htmlFor="" className="pr-4">
              {t("Province")} *
            </label>
            <select
              {...register("provinces", {
                required: t("required"),
              })}
              value={selectedProvince ?? ""}
              className="mt-2"
              tabIndex={13}
              onChange={(e) => setSelectedProvince(e.target.value)}
            >
              <option value="" disabled>
                {t("choose your province")}
              </option>
              {Provinces.map(({ name, id }) => (
                <option value={id} key={id}>
                  {name}
                </option>
              ))}
            </select>
            <p className="absolute left-0 text-red-500">
              {errors.provinces?.message}
            </p>
          </div>

          <div className="form-group flex flex-col xs:w-full sm:w-1/3 rtl text-right mt-6 relative">
            <label htmlFor="" className="pr-4">
              {t("City")} *
            </label>
            <select
              tabIndex={14}
              className="mt-2"
              {...register("city", {
                required: t("required"),
                maxLength: { value: 50, message: "" },
              })}
              onChange={(e) => setSelectedCity(e.target.value)}
            >
              <option value="" disabled selected>
                {t("choose your city")}
              </option>
              {Cities.map(({ id, name, provinceId }) =>
                selectedProvince === `${provinceId}` ? (
                  <option value={id} key={id}>
                    {name}
                  </option>
                ) : null
              )}
            </select>
            <p className="absolute left-0 text-red-500">
              {errors.city?.message}
            </p>
          </div>

          <div className="form-group flex flex-col xs:w-full sm:w-1/3 rtl text-right mt-6 relative">
            <label htmlFor="" className="pr-4">
              {t("Street")} *
            </label>
            <input
              maxLength={50}
              {...register("byStreet", {
                required: t("required"),
                maxLength: { value: 50, message: "" },
              })}
              type="text"
              className="border-2 p-4 rounded-lg mt-2 text-right tracking-wider"
              tabIndex={15}
              placeholder={t("enter your street")}
            />
            <p className="absolute left-0 text-red-500">
              {errors.byStreet?.message}
            </p>
          </div>
        </div>

        <div className="form-section rtl flex flex-grow flex-wrap sm:flex-row mb-10 address-fields-section 2xl:flex-nowrap 2xl:gap-4">
          <div className="form-group flex flex-col w-full xs:w-1/2 md:w-1/3 xl:w-1/3 2xl:w-1/4 rtl text-right mt-6 relative">
            <label htmlFor="" className="pr-4">
              {t("Last alley")} *
            </label>
            <input
              tabIndex={16}
              maxLength={50}
              {...register("lastAlley", {
                required: t("required"),
                maxLength: { value: 50, message: "" },
              })}
              type="text"
              className="border-2 p-4 rounded-lg mt-2 text-right tracking-wider"
              placeholder={t("enter your last alley")}
            />
            <p className="absolute left-0 text-red-500">
              {errors.lastAlley?.message}
            </p>
          </div>
          <div className="form-group flex flex-col w-full xs:w-1/2 md:w-1/3 xl:w-1/3 2xl:w-1/4 rtl text-right mt-6 relative">
            <label htmlFor="" className="pr-4">
              {t("Plaque")}*
            </label>
            <input
              tabIndex={17}
              maxLength={10}
              {...register("plaqueNo", {
                required: t("required"),
              })}
              type="text"
              className="border-2 p-4 rounded-lg mt-2 text-right tracking-wider"
              placeholder={t("enter your plaque")}
            />
            <p className="absolute left-0 text-red-500">
              {errors.plaqueNo?.message}
            </p>
          </div>
          <div className="form-group flex flex-col w-full xs:w-1/2 md:w-1/3 xl:w-1/3 2xl:w-1/4 rtl text-right mt-6 relative">
            <label htmlFor="" className="pr-4">
              {t("Unit NO")}*
            </label>
            <input
              tabIndex={18}
              maxLength={10}
              {...register("unitNo", {
                required: t("required"),
              })}
              type="text"
              className="border-2 p-4 rounded-lg mt-2 text-right tracking-wider"
              placeholder={t("enter your unit NO")}
            />
            <p className="absolute left-0 text-red-500">
              {errors.unitNo?.message}
            </p>
          </div>
          {(userData.code == null || userData.hasSubAgents) && (
            <div className="form-group flex flex-col w-full xs:w-1/2 md:w-1/3 xl:w-1/3 2xl:w-1/4 rtl text-right mt-6 relative">
              <label htmlFor="" className="pr-4">
                {t("AgentCode")}
              </label>
              <input
                tabIndex={18}
                maxLength={10}
                {...register("agentCode")}
                type="text"
                className="border-2 p-4 rounded-lg mt-2 text-right tracking-wider"
                placeholder={t("enter your agentCopde")}
              />
              <p className="absolute left-0 text-red-500">
                {errors.agentCode?.message}
              </p>
            </div>
          )}
        </div>

        <div className="form-section rtl flex flex-grow xs:flex-col sm:flex-row gap-4 justify-between">
          <div className="rule-section flex algin-center items-center gap-2">
            <input
              id="role-checkbox"
              type="checkbox"
              tabIndex={19}
              disabled={checkBoxToggle}
              checked={!toggle}
              onChange={() => setToggle(!toggle)}
            />
            <label className="role-checkbox-label" htmlFor="role-checkbox">
              <p className="w-50">
                {t("I had read and accept the contract", {
                  name: `${firstName ?? ""} ${lastName ?? ""}`,
                  nationalCode: localizeNumbers(nationalCode),
                  service: getServiceTypeName(selectedType),
                  username: isCompany
                    ? localizeNumbers(nationalIdentity)
                    : localizeNumbers(cellPhone),
                })}
                <small>
                  <Link
                    to="https://internet.sepanta.com/docs/CustomerContract.pdf"
                    className="btn-show-role font-bold mr-1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ({t("Read contract")})
                  </Link>
                </small>
              </p>
            </label>
          </div>
        </div>

        <div className="rule-section flex algin-center items-center gap-2 mt-4">
          <button
            id="signup-btn"
            type="submit"
            disabled={toggle || isSubmitting}
            className={`signup-btn p-5 flex items-center justify-center gap-2 rounded-md ${
              toggle || isSubmitting
                ? "opacity-50 cursor-not-allowed bg-gray-400"
                : "bg-blue-500 text-white cursor-pointer"
            }`}
          >
            {isSubmitting ? (
              <div className="text-center w-full py-2">
                <div className="w-6 h-6 border-2 border-solid border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="mt-2 text-sm">در حال ارسال...</p>
              </div>
            ) : (
              <>
                {t("Accept & Continue")}
                <span className="sepanta-icon-left-long-arrow"></span>
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}
